import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CommonService } from "../../services/CommonService";
import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { formatDate, formatDateToShow, useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";
import { SystemAccessSetupValidationSchema } from "./validator";
import { SystemAccessSetupFieldsPanel } from "./Panels/SystemAccessSetupFieldsPanel";
import { SystemAccessSetupToolbar } from "./SystemAccessSetupToolbar";
import { ISystemAccessSetup } from "../../models/SystemAccessSetup";
import { EmployeeService } from "../../services/EmployeeService";







export interface ISystemAccess { }
const SINGLE_RECORD_DATA_QUERY_KEY = "system-access-single-data-record";
const SYSTEM_ACCESS_LIST_DATA_QUERY_KEY = "system-access-list";

const useGetSystemAccessData = (
	// clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, id, navigateToLogout],
		queryFn: () => {
			const url = id && `/v1/system-access/${id}/`
			return CommonService.getById(url, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useSystemAccess = (
	// clientId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SYSTEM_ACCESS_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {
			const url = `/v1/system-access/?order_by=${params.order_by}&order_direction=${params.order_direction}&page=${params.page}&size=${params.size}&search_query=${params.search_query}`
			return CommonService.getList(
				url,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["Employee-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

export const SystemAccessSetup: React.FC<ISystemAccess> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [systemAccessId, setSystemAccessId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "name",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "name",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const formMethods = useForm<any>({
		resolver: yupResolver(SystemAccessSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const { isLoading: isLoadingemployeeDDL, data: employeeDDL } = useGetEmployeeDDL(clientId);

	const { isPending: isPendingById, data: selectedDataRecord } = useGetSystemAccessData(
		editRecordId,
		navigateToLogout
	);

	const { isPending, data } = useSystemAccess(
		queryParams,
		navigateToLogout
	);
	const systemAccessList: any | null = data;

	const updatedSystemAccessListItems = useMemo(() => {
		return (
			systemAccessList &&
			systemAccessList.items &&
			systemAccessList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/system-access/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.name}
					</Link>
				),
			}))
		);
	}, [systemAccessList]);

	const { isPending: isSubmitting, mutate: submitSystemAccessFormFields } =
		useMutation({
			mutationFn: (variables: any) =>
				CommonService.create(
					variables.createUrl,
					variables.updateUrl,
					variables.payload,
					variables.navigateToLogout,
				),
			onSuccess: (res: IServerError | IResponse | any) => {

				if (res.status === 200) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/system-access/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [SYSTEM_ACCESS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {

					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/system-access/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [SYSTEM_ACCESS_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const onSubmit = (values: any) => {
		const payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			accessLevel: values.accessLevel,
			employeeId: values.employeeId,
			firstName: values.firstName,
			lastName: values.lastName,
			email: values.email,
			status: values.status,
			SystemAccessThisScreen: values.SystemAccessThisScreen,
			InvoiceAndBankReports: values.InvoiceAndBankReports,
			CanSubmitDirectDepositEtc: values.CanSubmitDirectDepositEtc,
			CanUnmaskEncryptedData: values.CanUnmaskEncryptedData,
		};
		const ApiCredentials = {
			createUrl: `/v1/system-access/`,
			updateUrl: id && `/v1/system-access/${id}/`,
			payload: payload,
			navigateToLogout: navigateToLogout,
		}
		submitSystemAccessFormFields(ApiCredentials);
		console.log("payload", payload)
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteSystemAccessById } = useMutation({
		mutationFn: (values: any) => CommonService.deleteById(
			values.url,
			values.navigateToLogout
		),

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/system-access`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [SYSTEM_ACCESS_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteSystemAccessField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			// let delPayload = {
			// 	systemAccessId: systemAccessId,
			// 	navigateToLogout: navigateToLogout,
			// };			
			let deleteApiCredentials = {
				url: `/v1/system-access/${systemAccessId}/`,
				navigateToLogout: navigateToLogout,
			}
			deleteSystemAccessById(deleteApiCredentials);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setSystemAccessId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setSystemAccessId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveSystemAccessById } = useMutation({
		mutationFn: (values: any) => CommonService.archive(
			values.url,
			values.navigateToLogout
		),

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {

				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/system-access`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [SYSTEM_ACCESS_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveSystemAccessField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				systemAccessId: systemAccessId,
				navigateToLogout: navigateToLogout,
			};
			let archiveApiCredentials = {
				url: `/v1/system-access/`,
				navigateToLogout: navigateToLogout,
			}
			archiveSystemAccessById(archiveApiCredentials);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setSystemAccessId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/system-access`;
		navigate(path);
	};

	const expandedLabels = ["Settings", "System Access"];
	const expandedSystemAccessOrSetupItem = "System Access"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "System Access" &&
				item.label === expandedSystemAccessOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedSystemAccessOrSetupItem && "/system-access",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		SystemAccessName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: ISystemAccessSetup | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/system-access/${params.value}`;
		navigate(path);
	}, []);

	return (
		<Grid>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive || isLoadingemployeeDDL}
				testId="system-access Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteSystemAccessField}
				cancelActionHandler={onCloseHandler}
				testId="system-access-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 5, lg: 4.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="System Access"
										testId={`system-access`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<SystemAccessSetupFieldsPanel
											employeeDDL={employeeDDL}
										/>
									</StandardPanel>
								}
								toolbar={
									<SystemAccessSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}></Grid>
						{/* <Grid
							size={{ xs: 12, sm: 12, md: 5, lg: 4.1, xl: 3.1 }}
							sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
						> */}
						<Grid
							size={{ xs: 12, sm: 12, md: 4.5, lg: 4.5, xl: 4.5 }}
							sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
						>
							<DDLDataTable
								rows={
									systemAccessList && updatedSystemAccessListItems
										? updatedSystemAccessListItems
										: []
								}
								columns={[
									{ field: "label", headerName: "Name" },
									{ field: "userName", headerName: "User Name" },
									{ field: "status", headerName: "Status" },
									{ field: "accessLevel", headerName: "Access Level" },
									{ field: "lastLogin", headerName: "Last Login " },
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								showArchiveIcon={false}
								showPaginator={false}
								isLoading={isPending}
								totalRecords={
									systemAccessList && systemAccessList.total
										? systemAccessList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveSystemAccessField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"system-access-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
